import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiError } from '@/api/request'
import { Field, NativeSelect } from '@/components/common/Field'
import { errorMessage } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useProducts } from '@/hooks/useDirectory'
import { useLinkRecord } from '@/hooks/useTickets'
import { useToast } from '@/providers/ToastProvider'

interface RecordLinkDialogProps {
  routeId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Inserts text at the composer caret (the key, when asked or when linking isn't live). */
  onInsertText?: (text: string) => void
}

/** "Insert record link": a plain reference `{ product, type, key, url }` (plan §10). */
export function RecordLinkDialog({ routeId, open, onOpenChange, onInsertText }: RecordLinkDialogProps) {
  const { t } = useTranslation()
  const { notify } = useToast()
  const products = useProducts(open)
  const link = useLinkRecord(routeId)
  const [product, setProduct] = useState('')
  const [type, setType] = useState('')
  const [key, setKey] = useState('')
  const [url, setUrl] = useState('')
  const [insert, setInsert] = useState(true)
  const [missing, setMissing] = useState(false)

  const productKey = product || products.data?.[0]?.key || ''
  const pattern = products.data?.find((p) => p.key === productKey)?.keyPatterns?.[0]

  function close() {
    onOpenChange(false)
    setType('')
    setKey('')
    setUrl('')
    setMissing(false)
    link.reset()
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    const recordType = type.trim() || pattern?.type || ''
    if (!recordType || !key.trim() || !productKey) {
      setMissing(true)
      return
    }
    link.mutate(
      { product: productKey, type: recordType, key: key.trim(), url: url.trim() || undefined },
      {
        onSuccess: () => {
          if (insert) onInsertText?.(key.trim())
          close()
        },
        onError: (err) => {
          // Endpoint not live yet: fall back to a plain textual reference in the message.
          if (err instanceof ApiError && (err.status === 404 || err.status === 405)) {
            onInsertText?.(key.trim())
            notify({ title: t('recordLink.notAvailable'), tone: 'warning' })
            close()
          }
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('recordLink.title')}</DialogTitle>
          <DialogDescription>{t('recordLink.description')}</DialogDescription>
        </DialogHeader>
        <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
          <div className="grid grid-cols-2 gap-4">
            <Field id="rl-product" label={t('recordLink.product')}>
              <NativeSelect id="rl-product" value={productKey} onChange={(e) => setProduct(e.target.value)}>
                {products.data?.map((p) => (
                  <option key={p.id} value={p.key}>
                    {p.name}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field id="rl-type" label={t('recordLink.type')}>
              <Input
                id="rl-type"
                value={type}
                placeholder={pattern?.type ?? t('recordLink.typePlaceholder')}
                onChange={(e) => setType(e.target.value)}
              />
            </Field>
          </div>
          <Field id="rl-key" label={t('recordLink.key')} error={missing && !key.trim() ? t('common.required') : null}>
            <Input
              id="rl-key"
              className="font-mono"
              value={key}
              placeholder={t('recordLink.keyPlaceholder')}
              onChange={(e) => setKey(e.target.value)}
            />
          </Field>
          <Field id="rl-url" label={`${t('recordLink.url')} (${t('common.optional')})`}>
            <Input id="rl-url" type="url" value={url} onChange={(e) => setUrl(e.target.value)} />
          </Field>
          {onInsertText ? (
            <label className="flex items-center gap-2 text-meta text-ink">
              <input type="checkbox" checked={insert} onChange={(e) => setInsert(e.target.checked)} />
              {t('recordLink.insertAsText')}
            </label>
          ) : null}
          {link.error && !(link.error instanceof ApiError && (link.error.status === 404 || link.error.status === 405)) ? (
            <p role="alert" className="rounded-md bg-status-escalated-bg px-3 py-2 text-meta text-status-escalated-fg">
              {t('common.error', { message: errorMessage(link.error) })}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={link.isPending}>
              {link.isPending ? t('recordLink.submitting') : t('recordLink.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
